import { FC } from 'react';

import { CheckboxFilterTree } from '@telialt/common-checkbox-filter-tree/src/CheckboxFilterTree';
import { ResponsiveLabel } from '@telialt/common-checkbox-filter-tree/src/LabelComponents';
import { FilterNode } from '@telialt/common-checkbox-filter-tree/src/model';
import { useTranslations } from '@telialt/common-cms/src/useTranslations';

import { FacetRange, FilterRange } from '../../model';
import { FacetItem } from '../FacetItem/FacetItem';
import { FacetContainer } from './Facets.styles';

type Props = {
    facet: FacetRange;
    filter: FilterRange;
    onChange: (fieldName: string, filter?: FilterRange) => void;
    showCount?: boolean;
};

const mapFacetItemsToFilterNodes = (showCount: boolean, items?: FacetRange['items']): FilterNode[] =>
    items?.map((item) => ({
        title: item.title,
        count: showCount ? item.count : undefined,
    }));

export const RangeFacet: FC<Props> = ({ facet, filter, onChange, showCount }) => {
    const { t } = useTranslations();

    const filters = mapFacetItemsToFilterNodes(showCount, facet.items);

    const handleSelect = (values: string[][]) => {
        const selectedValue = values?.[0]?.[0];
        const rangeItem = facet.items.find((item) => item.title === selectedValue);
        if (!rangeItem) {
            onChange(facet.key, undefined);
        } else {
            onChange(facet.key, { gte: rangeItem.from, lte: rangeItem.to });
        }
    };

    const selectedFacetItem = facet.items.find((item) => item.from === filter?.gte && item.to === filter?.lte);
    const selected = selectedFacetItem ? [[selectedFacetItem.title]] : undefined;

    return (
        <FacetItem id={facet.key} label={facet.label} facetType="range">
            <FacetContainer>
                <CheckboxFilterTree
                    selected={selected}
                    filters={filters}
                    selectionBehavior="single"
                    searchEnabled={facet.items.length > 10}
                    searchPlaceholder={t['search.results.facets.search_placeholder']}
                    searchAllyLabel={t['search.results.facets.search_a11y_label']}
                    searchClearButtonAllyLabel={t['search.results.facets.search_clear_label']}
                    maxVisibleItems={5}
                    showLessText={t['search.results.facets.collapse']}
                    showMoreText={t['search.results.facets.expand']}
                    onSelect={handleSelect}
                    Label={ResponsiveLabel}
                />
            </FacetContainer>
        </FacetItem>
    );
};
